import { Fragment } from 'react';
import { Controller, ControllerRenderProps, FieldValues, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { REQUIRED_TEXT_ERROR } from '@components/dynamic-input-fields/constants';
import { ErrorMessage } from '@components/error/error-message';
import { LabelPrimary, LabelSecondary } from '@components/label';
import { Select, SelectOptGroup, SelectOption } from '@components/select';
import {
  Admission,
  ADMISSION_TYPE_BOTTLE_SERVICE,
  ADMISSION_TYPE_GUEST_LIST,
  ADMISSION_TYPE_TICKET,
  AdmissionType,
} from '@domain/admission';
import { getDefaultOfferNameTranslation } from '@utils/get-default-offer-name-translation/get-default-offer-name-translation';
import { getDefaultOfferPluralNameTranslation } from '@utils/get-default-offer-name-translation/get-default-offer-plural-name-translation';
import { getTranslatedPropertyBasedOnOrder } from '@utils/get-translated-property-based-on-order/get-translated-property-based-on-order';
import { formatCurrency } from '@utils/price-formatter/format-currency';

import { formatLabel } from './utils/format-label';
import { InputSelectAdmissionProps } from './input-select-admission.type';

export const InputSelectAdmission = ({
  eventId,
  admissionList,
  selectedOffer,
  onChange,
}: InputSelectAdmissionProps) => {
  const { t, i18n } = useTranslation();
  const {
    control,
    formState: { errors },
  } = useFormContext();

  const handleAdmissionChange = (
    field: ControllerRenderProps<FieldValues, 'offerId'>,
    e: React.ChangeEvent<HTMLSelectElement>
  ) => {
    if (admissionList) {
      field.onChange(e.currentTarget.value);
      if (onChange) onChange();
    }
  };
  const admissionOrder = [ADMISSION_TYPE_TICKET, ADMISSION_TYPE_BOTTLE_SERVICE, ADMISSION_TYPE_GUEST_LIST];
  return (
    <Controller
      name='offerId'
      control={control}
      rules={{ required: t(REQUIRED_TEXT_ERROR) }}
      render={({ field }) => (
        <fieldset className='flex flex-col items-start'>
          <LabelPrimary htmlFor='offerId'>{t('offers:selectAnOffer')}</LabelPrimary>
          <Select
            {...field}
            hasError={!!errors.offerId}
            disabled={!eventId || !admissionList}
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) => handleAdmissionChange(field, e)}>
            <SelectOption label={t('offers:selectAnOffer')} value={''} disabled />
            {admissionList &&
              Object.entries(admissionList)
                .sort((a, b) => admissionOrder.indexOf(a[0]) - admissionOrder.indexOf(b[0]))
                .map(([key, value]) => {
                  const offerName = (offer: Admission, type: AdmissionType) =>
                    getTranslatedPropertyBasedOnOrder({
                      translations: offer.name,
                      preferredLanguage: i18n.language,
                    }) ?? t(getDefaultOfferNameTranslation(type));
                  return (
                    <Fragment key={key}>
                      {(value as Admission[]).length === 0 ? null : (
                        <>
                          <SelectOptGroup label={t(getDefaultOfferPluralNameTranslation(key))}>{key}</SelectOptGroup>
                          {(value as Admission[]).map((offer: Admission) => {
                            const isDisabled =
                              (offer.quantity !== 0 && offer.quantity === offer.soldItems) ||
                              offer.salesStatus.isSalesClosed ||
                              offer.salesStatus.isSoldOut;
                            return (
                              <SelectOption
                                key={offer.id}
                                value={offer.id}
                                disabled={isDisabled}
                                label={formatLabel({ offer, name: offerName(offer, key as AdmissionType), t })}
                              />
                            );
                          })}
                        </>
                      )}
                    </Fragment>
                  );
                })}
          </Select>
          {!!selectedOffer?.price.offlinePrice && (
            <LabelSecondary className='mt-1'>
              {t(selectedOffer?.price.onlinePrice ? 'offers:payOnPremiseValue' : 'offers:payOnDoorValue', {
                value: formatCurrency({
                  number: selectedOffer?.price.offlinePrice,
                  currency: selectedOffer.price.currency,
                }),
              })}
            </LabelSecondary>
          )}
          <ErrorMessage name='offerId' />
        </fieldset>
      )}
    />
  );
};
